import { useMemo, useState } from 'react'
import { format } from 'date-fns'
import { PiStack, PiX, PiClock, PiCaretDown, PiTrash } from 'react-icons/pi'

import FbButton from 'components/FbUI/FbButton'
import { Ds2026SearchInput } from 'components/FbUI/Ds2026SearchInput'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { Alert } from 'components/FbUI/Alert'
import { Skeleton } from 'components/FbUI/Skeleton'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { TabPanel } from 'components/FbUI/PillTabs'
import { cn } from 'components/UI/cn'

import {
  SequenceTemplateListItem,
  useApplySequenceTemplate,
  useDeleteSequenceTemplate,
  useSequenceTemplate,
  useSequenceTemplates,
} from './sequence_templates_api'

/**
 * In-builder Templates drawer (FIR-7633). Content-scoped overlay (absolute, not
 * viewport-fixed) so it dims the build step only and leaves the global nav
 * reachable — matching the homepage Action Center pattern.
 *
 * "First Bite library" is a coming-soon teaser until the seeded starter content
 * ships (separate ticket); flip FIRST_BITE_LIBRARY_COMING_SOON to false once it
 * has real content.
 */
const FIRST_BITE_LIBRARY_COMING_SOON = true

type DrawerTab = 'yours' | 'firstbite'

interface Props {
  open: boolean
  onClose: () => void
  campaignId: number
  /** Called after a template is applied so the builder can re-hydrate. */
  onApplied?: () => void
}

/** Skeleton placeholder mirroring a template card's layout — used for the
 *  coming-soon First Bite library teaser. */
function TemplateCardSkeleton() {
  return (
    <div className="rounded-lg border border-line bg-white p-3.5">
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="mt-2 h-4 w-2/5" />
      <Skeleton className="mt-2.5 h-4 w-4/5" />
      <div className="mt-3 flex items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-7 w-28 rounded-md" />
      </div>
    </div>
  )
}

export function SequenceTemplatesDrawer({
  open,
  onClose,
  campaignId,
  onApplied,
}: Props) {
  const [activeTab, setActiveTab] = useState<DrawerTab>('yours')
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [pendingDelete, setPendingDelete] =
    useState<SequenceTemplateListItem | null>(null)
  const [hoveredId, setHoveredId] = useState<number | null>(null)
  const [applyingId, setApplyingId] = useState<number | null>(null)

  const { data: templates = [], isLoading } = useSequenceTemplates(open)
  const { data: expandedDetail } = useSequenceTemplate(expandedId)
  const applyMutation = useApplySequenceTemplate(campaignId)
  const deleteMutation = useDeleteSequenceTemplate()

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return templates
    return templates.filter((t) => t.name.toLowerCase().includes(q))
  }, [templates, search])

  // Nothing to search on the coming-soon library tab, or before any template
  // has been saved on the Your-templates tab.
  const searchDisabled = activeTab === 'firstbite' || templates.length === 0

  if (!open) return null

  const handleApply = (template: SequenceTemplateListItem) => {
    setApplyingId(template.id)
    applyMutation.mutate(template.id, {
      onSuccess: () => {
        onApplied?.()
        onClose()
      },
      onSettled: () => setApplyingId(null),
    })
  }

  const handleConfirmDelete = () => {
    if (!pendingDelete) return
    const id = pendingDelete.id
    deleteMutation.mutate(id, {
      onSuccess: () => {
        if (expandedId === id) setExpandedId(null)
      },
    })
    setPendingDelete(null)
  }

  const renderCard = (template: SequenceTemplateListItem) => {
    const expanded = expandedId === template.id
    const isApplying = applyingId === template.id
    return (
      <div
        key={template.id}
        onMouseEnter={() => setHoveredId(template.id)}
        onMouseLeave={() =>
          setHoveredId((cur) => (cur === template.id ? null : cur))
        }
        className="rounded-lg border border-line bg-white p-3.5 transition-shadow hover:shadow-md"
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="min-w-0 truncate text-lg font-semibold text-neutral-800">
            {template.name}
          </h3>
          <button
            type="button"
            aria-label={`Delete ${template.name}`}
            onClick={() => setPendingDelete(template)}
            className={cn(
              '-mr-1 -mt-0.5 flex shrink-0 items-center justify-center rounded-lg p-1 text-neutral-500 transition hover:bg-neutral-100 hover:text-error-strong focus-visible:opacity-100',
              hoveredId === template.id ? 'opacity-100' : 'opacity-0'
            )}
          >
            <PiTrash className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-0.5 text-sm text-neutral-500">
          {template.step_count} step{template.step_count !== 1 ? 's' : ''} ·
          Created {format(new Date(template.created_at), 'MMM d, yyyy, h:mm a')}
        </p>
        {!!template.description && (
          <p className="mt-1.5 text-base text-neutral-500">
            {template.description}
          </p>
        )}

        {expanded &&
          (expandedDetail?.id === template.id ? (
            <ol className="mt-3 space-y-2.5 border-t border-line pt-3">
              {expandedDetail.steps.map((s, i) => (
                <li
                  key={`${template.id}-${i}`}
                  className="flex items-center gap-2.5 text-sm"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-xs font-medium text-neutral-600">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-neutral-700">
                    {s.email_subject}
                  </span>
                  <span className="shrink-0 text-neutral-400">
                    {i < expandedDetail.steps.length - 1
                      ? `${s.wait_in_days}d`
                      : '—'}
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <div className="mt-3 space-y-2 border-t border-line pt-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          ))}

        <div className="mt-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setExpandedId(expanded ? null : template.id)}
            className="flex items-center gap-1 text-sm font-medium text-neutral-500 hover:text-neutral-800"
          >
            <PiCaretDown
              className={cn(
                'h-3 w-3 transition-transform',
                expanded && 'rotate-180'
              )}
            />
            {expanded ? 'Hide steps' : 'Preview steps'}
          </button>
          <FbButton
            variant="primary"
            size="xs"
            className="text-sm"
            loading={isApplying}
            disabled={applyMutation.isPending}
            onClick={() => handleApply(template)}
          >
            Use template
          </FbButton>
        </div>
      </div>
    )
  }

  return (
    <Ds2026ButtonProvider>
      <div className="absolute inset-0 z-20">
        <button
          type="button"
          aria-label="Close templates"
          className="absolute inset-0 h-full w-full bg-black/30"
          onClick={onClose}
        />
        <div
          className="absolute right-0 top-0 flex h-full flex-col border-l border-line bg-surface shadow-xl"
          style={{ width: '600px', maxWidth: '92vw' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-2">
              <PiStack className="h-5 w-5 text-brand-active" />
              <h2 className="text-base font-semibold text-neutral-800">
                Sequence Templates
              </h2>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="flex flex-shrink-0 items-center justify-center rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-500"
            >
              <PiX className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs — divider inset to the content width via mx-4 */}
          <UnderlineTabs
            aria-label="Template source"
            idPrefix="seq-tpl-source"
            className="mx-4"
            tabs={
              [
                { key: 'yours', title: 'Your templates' },
                { key: 'firstbite', title: 'First Bite library' },
              ] as const
            }
            activeKey={activeTab}
            onChange={setActiveTab}
          />

          {/* Search */}
          <div className="px-4 pt-3">
            <Ds2026SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search templates"
              aria-label="Search templates"
              disabled={searchDisabled}
            />
          </div>

          {/* List */}
          <div
            className="flex-1 overflow-y-auto px-4 py-3"
            style={{ scrollbarGutter: 'stable' }}
          >
            <TabPanel<DrawerTab>
              idPrefix="seq-tpl-source"
              tabKey="yours"
              activeKey={activeTab}
            >
              {isLoading ? (
                <div className="space-y-2.5">
                  {[0, 1, 2].map((i) => (
                    <TemplateCardSkeleton key={i} />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <p className="px-4 py-12 text-center text-sm text-neutral-500">
                  {templates.length === 0
                    ? 'Your saved sequence templates will live here.'
                    : 'No templates match your search.'}
                </p>
              ) : (
                <div className="space-y-2.5">{filtered.map(renderCard)}</div>
              )}
            </TabPanel>
            <TabPanel<DrawerTab>
              idPrefix="seq-tpl-source"
              tabKey="firstbite"
              activeKey={activeTab}
            >
              {FIRST_BITE_LIBRARY_COMING_SOON ? (
                <Alert
                  color="default"
                  centered
                  icon={PiClock}
                  title="Coming soon"
                  description="First Bite's curated starter templates are on the way — soon you'll be able to start a campaign from a proven sequence."
                />
              ) : null}
            </TabPanel>
          </div>
        </div>

        {/* Delete confirm — DS2026 styling, danger action */}
        {pendingDelete && (
          <div className="absolute inset-0 z-30 flex items-center justify-center">
            <button
              type="button"
              aria-label="Cancel"
              className="absolute inset-0 h-full w-full bg-black/40"
              onClick={() => setPendingDelete(null)}
            />
            <div className="relative w-[440px] max-w-[92vw] rounded-2xl border border-line bg-surface px-10 py-6 shadow-xl">
              <button
                type="button"
                aria-label="Close"
                onClick={() => setPendingDelete(null)}
                className="absolute right-4 top-4 rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-500"
              >
                <PiX className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold tracking-[-0.2px] text-neutral-800">
                Delete template?
              </h2>
              <p className="mt-1 text-sm text-neutral-500">
                <span className="font-medium text-neutral-700">
                  {pendingDelete.name}
                </span>{' '}
                will be removed from your templates. This cannot be undone.
              </p>
              <div className="mt-8 flex gap-[16px]">
                <FbButton
                  variant="secondary"
                  onClick={() => setPendingDelete(null)}
                  className="flex-1 !h-[44px] !min-h-[44px]"
                >
                  Cancel
                </FbButton>
                <FbButton
                  variant="danger"
                  loading={deleteMutation.isPending}
                  onClick={handleConfirmDelete}
                  className="flex-1 !h-[44px] !min-h-[44px]"
                >
                  Delete
                </FbButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </Ds2026ButtonProvider>
  )
}
